import { FormEvent, useState } from 'react';
import {
  AccessPermission,
  Department,
  createDepartment,
  saveDepartmentPermissions,
  updateDepartment,
} from '../../../api/accessApi';
import ToolPicker from './ToolPicker';
import { useAsync } from './shared';

interface DepartmentsTabProps {
  departments: Department[];
  permissions: AccessPermission[];
  /** Permission names the signed-in user may grant. */
  grantable: string[];
  onChanged: () => void;
}

export default function DepartmentsTab({ departments, permissions, grantable, onChanged }: DepartmentsTabProps) {
  const [editing, setEditing] = useState<Department | null>(null);
  const [draft, setDraft] = useState<Set<string>>(new Set());
  const toggleDraft = (name: string) =>
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  /** Tools a department really gets: directly ticked ones plus every tool under a ticked "All <section>". */
  const countTools = (granted: string[]) => {
    const have = new Set(granted);
    const fullSections = new Set(permissions.filter((p) => p.kind === 'section' && have.has(p.name)).map((p) => p.section));
    return permissions.filter((p) => p.kind === 'tool' && (have.has(p.name) || fullSections.has(p.section))).length;
  };

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const { error, busy, run } = useAsync();

  const add = async (e: FormEvent) => {
    e.preventDefault();
    const created = await run(() => createDepartment({ name: name.trim(), code: code.trim() || undefined }));
    if (created) {
      setName('');
      setCode('');
      onChanged();
    }
  };

  const saveTools = async () => {
    if (!editing) return;
    const saved = await run(() => saveDepartmentPermissions(editing.id, Array.from(draft)));
    if (saved) {
      setEditing(null);
      onChanged();
    }
  };

  const toggleActive = async (o: Department) => {
    const updated = await run(() => updateDepartment(o.id, { is_active: !o.is_active }));
    if (updated) onChanged();
  };

  return (
    <section className="panel">
      <h2>Departments</h2>
      {error && <p className="acc-error">{error}</p>}
      <form className="acc-form acc-inline" onSubmit={add}>
        <label>
          Name
          <input required minLength={2} value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label>
          Code
          <input value={code} onChange={(e) => setCode(e.target.value)} />
        </label>
        <button className="btn primary" disabled={busy}>
          Add department
        </button>
      </form>
      {editing && (
        <div className="acc-tools">
          <h3>Tool access for {editing.name}</h3>
          <p className="note">
            Everyone in this department gets the ticked tools on top of their role. You can only grant tools you
            hold yourself.
          </p>
          <ToolPicker
            permissions={permissions}
            grantable={grantable}
            inherited={new Set()}
            draft={draft}
            onToggle={toggleDraft}
          />
          <div className="acc-tool-actions">
            <button className="btn primary" disabled={busy} onClick={saveTools}>
              Save tool access
            </button>
            <button className="btn" disabled={busy} onClick={() => setEditing(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}
      <div className="acc-scroll">
        <table className="acc-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Code</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {departments.map((o) => (
              <tr key={o.id} className={o.is_active ? '' : 'acc-off'}>
                <td>
                  <b>{o.name}</b>
                </td>
                <td>{o.code ?? ''}</td>
                <td>{o.is_active ? 'Active' : 'Inactive'}</td>
                <td className="acc-actions">
                  <button
                    className="btn"
                    disabled={busy}
                    onClick={() => {
                      setEditing(o);
                      setDraft(new Set(o.permissions));
                    }}
                  >
                    Tools{o.permissions.length > 0 ? ` (${countTools(o.permissions)})` : ''}
                  </button>
                  <button className="btn" disabled={busy} onClick={() => toggleActive(o)}>
                    {o.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {departments.length === 0 && <p className="note">No departments yet.</p>}
      </div>
    </section>
  );
}
